/* Access confirmation and navigation for the imported Teazy area only. */
export const SESSION_KEY = 'teazy:legacy:age-confirmed:v1';

const PAGE_VIEWS = Object.freeze({
  'about-us': 'teazy-legacy-about-us',
  faq: 'teazy-legacy-faq',
  contact: 'teazy-legacy-contact',
  'store-locator': 'teazy-legacy-store-locator',
  'location-page': 'teazy-legacy-location-page',
  landing: 'teazy-legacy-landing-page',
  'landing-page': 'teazy-legacy-landing-page',
  reviews: 'teazy-legacy-reviews',
  labs: 'teazy-legacy-labs',
});

/** Return an area URL, retaining variants, filters, pagination and fragments. */
export function areaUrl(href, currentUrl, root = '/') {
  if (!href || href.startsWith('#')) return href;
  let url;
  try { url = new URL(href, currentUrl); } catch { return href; }
  const current = new URL(currentUrl);
  if (url.origin !== current.origin || !/^https?:$/.test(url.protocol)) return href;
  const localeRoot = new URL(root, current.origin).pathname.replace(/\/$/, '');
  let path = url.pathname;
  if (localeRoot && (path === localeRoot || path.startsWith(`${localeRoot}/`))) {
    path = path.slice(localeRoot.length) || '/';
  }
  path = path.replace(/\/$/, '') || '/';
  const segments = path.split('/').filter(Boolean);
  let view;
  if (path === '/' || path === '/cart' || path === '/search' || path === '/collections') {
    view = 'teazy-legacy';
  } else if (['products', 'collections', 'blogs'].includes(segments[0]) && segments[1]) {
    view = 'teazy-legacy';
  } else if (segments[0] === 'pages' && segments[1] && segments.length === 2) {
    view = PAGE_VIEWS[segments[1]] || 'teazy-legacy';
  }
  if (!view) return href;
  url.searchParams.set('view', view);
  return `${url.pathname}${url.search}${url.hash}`;
}

export function hasConfirmation(storage) {
  try { return storage.getItem(SESSION_KEY) === 'yes'; } catch { return false; }
}

export function saveConfirmation(storage, confirmed) {
  try {
    if (confirmed) storage.setItem(SESSION_KEY, 'yes');
    else storage.removeItem(SESSION_KEY);
  } catch { /* Confirmation still works for this document when storage is blocked. */ }
}

export function initializeArea(doc, win) {
  const gate = doc.getElementById('S21AgeGate');
  const content = doc.getElementById('S21AreaContent');
  if (!gate || !content) return;
  const accept = gate.querySelector('[data-s21-accept]');
  const decline = gate.querySelector('[data-s21-decline]');
  const root = content.dataset.s21Root || '/';
  let storage;
  try { storage = win.sessionStorage; } catch { storage = null; }

  function unlock(moveFocus = false) {
    gate.hidden = true;
    content.inert = false;
    content.removeAttribute('aria-hidden');
    doc.documentElement.classList.add('s21-age-confirmed');
    if (moveFocus) {
      const main = doc.getElementById('MainContent');
      if (main) { main.setAttribute('tabindex', '-1'); main.focus({ preventScroll: true }); }
    }
  }

  function lock() {
    saveConfirmation(storage, false);
    content.inert = true;
    content.setAttribute('aria-hidden', 'true');
    doc.documentElement.classList.remove('s21-age-confirmed');
    gate.hidden = false;
    accept.focus({ preventScroll: true });
  }

  accept.hidden = false;
  accept.addEventListener('click', () => {
    saveConfirmation(storage, true);
    unlock(true);
  });
  decline.addEventListener('click', () => saveConfirmation(storage, false));
  gate.addEventListener('keydown', event => {
    if (event.key === 'Escape') { event.preventDefault(); return; }
    if (event.key !== 'Tab') return;
    if (event.shiftKey && doc.activeElement === decline) {
      event.preventDefault(); accept.focus();
    } else if (!event.shiftKey && doc.activeElement === accept) {
      event.preventDefault(); decline.focus();
    }
  });

  const editor = content.dataset.s21Editor === 'true';
  if (editor || hasConfirmation(storage)) unlock();
  else lock();
  win.addEventListener('pageshow', () => {
    if (editor || hasConfirmation(storage)) unlock();
    else if (gate.hidden) lock();
  });

  function scopeLink(link) {
    if (link.hasAttribute('data-s21-exit') || link.hasAttribute('download')) return;
    const oldHref = link.getAttribute('href');
    const scoped = areaUrl(oldHref, win.location.href, root);
    if (scoped !== oldHref) link.setAttribute('href', scoped);
  }

  function scopeForm(form) {
    if ((form.getAttribute('method') || 'get').toLowerCase() !== 'get') return;
    const action = form.getAttribute('action') || win.location.href;
    const scoped = areaUrl(action, win.location.href, root);
    const url = new URL(scoped, win.location.href);
    if (!url.searchParams.get('view')?.startsWith('teazy-legacy')) return;
    let input = form.querySelector('input[name="view"]');
    if (!input) { input = doc.createElement('input'); input.type = 'hidden'; input.name = 'view'; form.append(input); }
    input.value = url.searchParams.get('view');
  }

  function scopeTree(node) {
    if (!(node instanceof win.Element)) return;
    if (node.matches('a[href]')) scopeLink(node);
    if (node.matches('form')) scopeForm(node);
    node.querySelectorAll('a[href]').forEach(scopeLink);
    node.querySelectorAll('form').forEach(scopeForm);
  }
  scopeTree(content);
  const observer = new win.MutationObserver(records => {
    for (const record of records) {
      if (record.type === 'attributes') scopeLink(record.target);
      else record.addedNodes.forEach(scopeTree);
    }
  });
  observer.observe(content, { subtree: true, childList: true, attributes: true, attributeFilter: ['href'] });
  content.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    if (link) scopeLink(link);
    if (event.target.closest?.('[data-s21-recheck]')) lock();
  }, true);
  content.addEventListener('submit', event => scopeForm(event.target), true);
}

if (typeof document !== 'undefined') initializeArea(document, window);
